class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Initialize cross-sell on page load
    this.renderCrossSell();

    // Re-render cross-sell after cart quantity changes
    document.addEventListener('cart:change', () => this.renderCrossSell());
  }

  renderCrossSell() {
    const crossSellContainer = document.getElementById('CartDrawer-CrossSellItems');
    const crossSellSection = document.getElementById('CartDrawer-CrossSell');
    if (!crossSellContainer || !crossSellSection) return;

    const firstCartItem = document.querySelector('.cart-item[data-product-id]');
    if (!firstCartItem) {
      crossSellSection.style.display = 'none';
      return;
    }

    const productId = firstCartItem.dataset.productId;
    const cartProductIds = Array.from(document.querySelectorAll('.cart-item[data-product-id]'))
      .map(item => item.dataset.productId);

    fetch(`${routes.product_recommendations_url}?product_id=${productId}&limit=8`)
      .then(response => response.json())
      .then(data => {
        const recommendations = (data.products || []).filter(p =>
          p.available && !cartProductIds.includes(String(p.id))
        ).slice(0, 4);

        if (recommendations.length === 0) {
          crossSellSection.style.display = 'none';
          return;
        }

        crossSellSection.style.display = '';
        crossSellContainer.innerHTML = recommendations.map(product => {
          const variantId = product.variants && product.variants[0] ? product.variants[0].id : null;
          if (!variantId) return '';
          const price = (product.price / 100).toFixed(2);
          const imgSrc = product.featured_image || '';
          const separator = imgSrc.includes('?') ? '&' : '?';
          const sizedImg = imgSrc ? `${imgSrc}${separator}width=120` : '';
          return `
            <div class="cart-drawer__cross-sell-item">
              <a href="${product.url}" class="cart-drawer__cross-sell-link">
                <img src="${sizedImg}" alt="${product.title}" class="cart-drawer__cross-sell-image" width="120" height="120" loading="lazy">
                <p class="cart-drawer__cross-sell-name">${product.title}</p>
                <p class="cart-drawer__cross-sell-price">$${price}</p>
              </a>
              <button class="cart-drawer__cross-sell-add" data-variant-id="${variantId}">Add to cart</button>
            </div>
          `;
        }).join('');

        crossSellContainer.querySelectorAll('.cart-drawer__cross-sell-add').forEach(btn => {
          btn.addEventListener('click', (e) => this.handleCrossSellAdd(e));
        });
      })
      .catch(() => {
        crossSellSection.style.display = 'none';
      });
  }

  handleCrossSellAdd(event) {
    const button = event.currentTarget;
    const variantId = parseInt(button.dataset.variantId, 10);
    button.classList.add('adding');
    button.textContent = 'Adding...';

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: body
    })
      .then(response => response.json())
      .then(parsedState => {
        this.renderContents(parsedState);
      })
      .catch(() => {
        button.classList.remove('adding');
        button.textContent = 'Add to cart';
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.renderCrossSell();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
